<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useDataStore } from '@/stores/useDataStore';
import { useAuthStore } from '@/stores/useAuthStore';
import Maintenance from '@/components/Maintenance.vue';

const dataStore = useDataStore();
const authStore = useAuthStore();

const showTaxonomic = ref(false);
const selectedStrainId = ref<number | null>(null);

const showEnsembl = ref(false);
const showAlgaebase = ref(false);
const showWikimedia = ref(false);
const showGeo = ref(false);

const orderedEnsemblKeys = [
  'species.scientific_name',
  'species.wikipedia_url',
  'assembly.name',
  'assembly.accession',
  'assembly.provider_name',
  'assembly.default',
  'genebuild.method',
  'genebuild.version',
  'genebuild.initial_release_date',
  'genebuild.start_date',
];

const ensemblKeyMapping: Record<string, string> = {
  'species.scientific_name': 'Species/Scientific Name',
  'species.wikipedia_url': 'Species/Wikipedia URL',
  'assembly.name': 'Assembly/Name',
  'assembly.accession': 'Assembly/Accession',
  'assembly.provider_name': 'Assembly/Provider Name',
  'assembly.default': 'Assembly/Default',
  'genebuild.method': 'Genebuild/Method',
  'genebuild.version': 'Genebuild/Version',
  'genebuild.initial_release_date': 'Genebuild/Initial Release Date',
  'genebuild.start_date': 'Genebuild/Start Date',
};

const algaebaseKeyMapping: Record<string, string> = {
  'publication_details': 'Publication Details',
  'type_species': 'Type Species',
  'status_of_name': 'Type of Name',
  'type_information': 'Type Information',
  'general_environment': 'General Environnement',
};

const orderedWikimediaKeys = [
  'wikipedia_title',
  'wikipedia_title_length',
];

const wikimediaKeyMapping: Record<string, string> = {
  'wikipedia_title': 'Wikipedia Title',
  'wikipedia_title_length': 'Wikipedia Title Length',
};

const geoBaseKeyMapping: Record<string, string> = {
  'title': 'Title',
  'summary': 'Summary',
  'design': 'Design',
  'platform': 'Platform',
  'sample_count': 'Sample Count',
  'release_date': 'Release Date',
  'organism': 'Organism',
  'type': 'Type',
};

onMounted(async () => {
  await dataStore.loadBasicData();
});

const selectStrain = async (strainId: number) => {
  selectedStrainId.value = strainId;
  await dataStore.loadEnsemblForStrain(strainId);
  await dataStore.loadAlgaebaseForStrain(strainId);
  await dataStore.loadGeoForStrain(strainId);
  await dataStore.loadWikimediaForStrain(strainId);
  showEnsembl.value = true;
  showAlgaebase.value = true;
  showWikimedia.value = true;
  showGeo.value = true;
};

const closeEnsembl = () => {
  showEnsembl.value = false;
};

const closeAlgaebase = () => {
  showAlgaebase.value = false;
};

const closeWikimedia = () => {
  showWikimedia.value = false;
};

const closeGeo = () => {
  showGeo.value = false;
};

const getDisplayKey = (key: string, mapping: Record<string, string>): string => {
  const parts = key.split('_');
  const baseKey = parts.length > 1 ? parts.slice(-1)[0] : key;
  return mapping[baseKey] || baseKey;
};

// Computed pour ordonner les métadonnées par l'ordre désiré
const orderedEnsemblMetadata = computed((): EnsemblSpeciesMetadata[] => {
  return orderedEnsemblKeys
    .map(key => dataStore.ensemblMetadata.find(meta => meta.meta_key === key))
    .filter((meta): meta is EnsemblSpeciesMetadata => meta !== undefined);
});

const orderedWikimediaMetadata = computed((): WikimediaSpeciesMetadata[] => {
  return orderedWikimediaKeys
    .map(key => dataStore.wikimediaMetadata.find(meta => meta.meta_key === key))
    .filter((meta): meta is WikimediaSpeciesMetadata => meta !== undefined);
});

const geoMetadata = computed((): GeoSpeciesMetadata[] => {
  return [...dataStore.geoMetadata].sort((a, b) => {
    const baseA = a.meta_key.split('_').pop() || '';
    const baseB = b.meta_key.split('_').pop() || '';
    return baseA.localeCompare(baseB);
  });
});


</script>

<template>
  <Maintenance />
  <div v-if="authStore.isAuthenticated && authStore.scopes.includes('reader')">
    <div>
      <h1>Algothèque</h1>
      <button class="custom-btn" @click="showTaxonomic = !showTaxonomic">
        {{ showTaxonomic ? '📊' : '📋' }}
      </button>
    </div>

    <div class="module">
      <table class="styled-table">
        <thead>
          <tr>
            <th class="recoltes small">{{ $t('extended_code') }}</th>

            <th v-if="showTaxonomic" class="recoltes small">{{ $t('domain') }}</th>
            <th v-if="showTaxonomic" class="recoltes small">{{ $t('kingdom') }}</th>
            <th v-if="showTaxonomic" class="recoltes small">{{ $t('phylum') }}</th>
            <th v-if="showTaxonomic" class="recoltes small">{{ $t('specie_class') }}</th>
            <th v-if="showTaxonomic" class="recoltes small">{{ $t('superorder') }}</th>
            <th v-if="showTaxonomic" class="recoltes small">{{ $t('specie_order') }}</th>
            <th v-if="showTaxonomic" class="recoltes small">{{ $t('family') }}</th>
            <th v-if="showTaxonomic" class="recoltes small">{{ $t('genus') }}</th>
            <th v-if="showTaxonomic" class="recoltes small">{{ $t('specie') }}</th>

            <th v-if="!showTaxonomic" class="recoltes small">{{ $t('code') }}</th>
            <th v-if="!showTaxonomic" class="recoltes small">{{ $t('origin') }}</th>
            <th v-if="!showTaxonomic" class="recoltes small">{{ $t('sub_code') }}</th>
            <th v-if="!showTaxonomic" class="recoltes small">{{ $t('code_order') }}</th>

            <th v-if="!showTaxonomic" class="recoltes small">{{ $t('reception_date') }}</th>
            <th v-if="!showTaxonomic" class="recoltes small">{{ $t('collection') }}</th>
            <th v-if="!showTaxonomic" class="recoltes small">{{ $t('lot') }}</th>
            <th v-if="!showTaxonomic" class="recoltes small">{{ $t('strain_origin') }}</th>
            <th v-if="!showTaxonomic" class="recoltes small">{{ $t('isolation_year') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="strain in dataStore.strains" :class="{ selected: selectedStrainId === strain.id }"
            style="cursor: pointer;" @click="selectStrain(strain.id)" :key="strain.id">
            <td>{{ strain.extended_code }}</td>

            <td v-if="showTaxonomic">{{ strain.domain }}</td>
            <td v-if="showTaxonomic">{{ strain.kingdom }}</td>
            <td v-if="showTaxonomic">{{ strain.phylum }}</td>
            <td v-if="showTaxonomic">{{ strain.specie_class }}</td>
            <td v-if="showTaxonomic">{{ strain.superorder }}</td>
            <td v-if="showTaxonomic">{{ strain.specie_order }}</td>
            <td v-if="showTaxonomic">{{ strain.family }}</td>
            <td v-if="showTaxonomic">{{ strain.genus }}</td>
            <td v-if="showTaxonomic">{{ strain.species }}</td>

            <td v-if="!showTaxonomic">{{ strain.code }}</td>
            <td v-if="!showTaxonomic">{{ strain.origin }}</td>
            <td v-if="!showTaxonomic">{{ strain.sub_code }}</td>
            <td v-if="!showTaxonomic">{{ strain.code_order }}</td>

            <td v-if="!showTaxonomic">{{ strain.reception_date }}</td>
            <td v-if="!showTaxonomic">{{ strain.collection }}</td>
            <td v-if="!showTaxonomic">{{ strain.lot }}</td>
            <td v-if="!showTaxonomic">{{ strain.strain_origin }}</td>
            <td v-if="!showTaxonomic">{{ strain.isolation_year }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Ensembl Metadata Display -->
    <div v-if="showEnsembl" class="module">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <h2>Données Ensembl pour la souche {{dataStore.strains.find(s => s.id === selectedStrainId)?.extended_code}}
          ({{ dataStore.ensemblMetadata[0]?.species_name || 'N/A' }})</h2>
        <span style="cursor: pointer; font-size: 24px; color: #666; font-weight: bold;"
          @click="closeEnsembl">&times;</span>
      </div>
      <div v-if="selectedStrainId">
        <table class="styled-table">
          <thead>
            <tr>
              <th>Clé</th>
              <th>Valeur</th>
              <th>Date de récupération</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="meta in orderedEnsemblMetadata" :key="meta.id">
              <td>{{ getDisplayKey(meta.meta_key, ensemblKeyMapping) }}</td>
              <td style="white-space: pre-wrap; word-break: break-word;">{{ meta.meta_value }}</td>
              <td>{{ meta.fetched_at }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="orderedEnsemblMetadata.length === 0">Aucune donnée Ensembl disponible pour cette souche.</p>
      </div>
    </div>

    <!-- AlgaeBase Metadata Display -->
    <div v-if="showAlgaebase" class="module">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <h2>Données AlgaeBase pour la souche {{dataStore.strains.find(s => s.id === selectedStrainId)?.extended_code}}
          ({{ dataStore.ensemblMetadata[0]?.species_name || 'N/A' }})</h2>
        <span style="cursor: pointer; font-size: 24px; color: #666; font-weight: bold;"
          @click="closeAlgaebase">&times;</span>
      </div>
      <div v-if="selectedStrainId">
        <table class="styled-table">
          <thead>
            <tr>
              <th>Clé</th>
              <th>Valeur</th>
              <th>Date de récupération</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="meta in dataStore.algaebaseMetadata" :key="meta.id">
              <td>{{ getDisplayKey(meta.meta_key, algaebaseKeyMapping) }}</td>
              <td style="white-space: pre-wrap; word-break: break-word;">{{ meta.meta_value }}</td>
              <td>{{ meta.fetched_at }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="dataStore.algaebaseMetadata.length === 0">Aucune donnée AlgaeBase disponible pour cette souche.</p>
      </div>
    </div>

    <!-- Wikimedia Metadata Display -->
    <div v-if="showWikimedia" class="module">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <h2>Données Wikimedia pour la souche {{dataStore.strains.find(s => s.id === selectedStrainId)?.extended_code}}
          ({{ dataStore.wikimediaMetadata[0]?.species_name || 'N/A' }})</h2>
        <span style="cursor: pointer; font-size: 24px; color: #666; font-weight: bold;"
          @click="closeWikimedia">&times;</span>
      </div>
      <div v-if="selectedStrainId">
        <table class="styled-table">
          <thead>
            <tr>
              <th>Clé</th>
              <th>Valeur</th>
              <th>Date de récupération</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="meta in orderedWikimediaMetadata" :key="meta.id">
              <td>{{ getDisplayKey(meta.meta_key, wikimediaKeyMapping) }}</td>
              <td style="white-space: pre-wrap; word-break: break-word;">{{ meta.meta_value }}</td>
              <td>{{ meta.fetched_at }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="orderedWikimediaMetadata.length === 0">Aucune donnée Wikimedia disponible pour cette souche.</p>
      </div>
    </div>

    <!-- New: Geo Metadata Display -->
    <div v-if="showGeo" class="module">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <h2>Données GEO pour la souche {{dataStore.strains.find(s => s.id === selectedStrainId)?.extended_code}} ({{
          dataStore.geoMetadata[0]?.species_name || 'N/A' }})</h2>
        <span style="cursor: pointer; font-size: 24px; color: #666; font-weight: bold;" @click="closeGeo">&times;</span>
      </div>
      <div v-if="selectedStrainId">
        <table class="styled-table">
          <thead>
            <tr>
              <th>Clé</th>
              <th>Valeur</th>
              <th>Date de récupération</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="meta in geoMetadata" :key="meta.id">
              <td>{{ getDisplayKey(meta.meta_key, geoBaseKeyMapping) }}</td>
              <td style="white-space: pre-wrap; word-break: break-word;">{{ meta.meta_value }}</td>
              <td>{{ meta.fetched_at }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="geoMetadata.length === 0">Aucune donnée GEO disponible pour cette souche.</p>
      </div>
    </div>

  </div>

  <div v-else class="centered-block">
    <h2>{{ $t('loginRequiredTitle') }}</h2>
    <p>{{ $t('loginRequiredMessage') }}</p>
  </div>
</template>

<style scoped>
.selected {
  background-color: #e3f2fd;
}
</style>